import React, { useState, useEffect } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import LayoutSelector2025 from '../../components/LayoutSelector2025';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import HolisticTestimonialsManagement from '../WidgetEditComponents/HolisticTestimonialsManagement';
import ImageTooBigPopup from '../WidgetEditComponents/ImageTooBigPopup';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function HolisticTestimonialEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // ✅ RACE CONDITION PREVENTION - KRITISCH für Widget-Switches
    const [isInitialized, setIsInitialized] = useState(false);
    const [previousLayoutType, setPreviousLayoutType] = useState(editData.layoutType);
    
    // Upload States
    const [uploadingStates, setUploadingStates] = useState({});
    const [showImageTooBigPopup, setShowImageTooBigPopup] = useState(false);
    const [tooBigFileInfo, setTooBigFileInfo] = useState({ size: 0, name: '' });
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);
    
    const setUploadingState = (index, isUploading) => {
        setUploadingStates(prev => ({
            ...prev,
            [index]: isUploading
        }));
    };
    
    // ✅ LAYOUT CHANGE HANDLER
    const handleLayoutChange = (layoutId, layoutConfig) => {
        console.log(`🎨 HolisticTestimonial layout change: ${layoutId}`, layoutConfig);
        const fakeEvent = {
            target: { name: 'layoutType', value: layoutId, type: 'text' }
        };
        handleInputChange(fakeEvent);
    };

    // ✅ INITIALIZATION EFFECT - Set initialized flag after first render
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialized(true);
            console.log('🚀 HolisticTestimonial initialized - layout variations now active');
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

    // ✅ LAYOUT VARIATIONS SYSTEM mit Race Condition Prevention
    useEffect(() => {
        const currentLayout = editData.layoutType;
        
        // ✅ CRITICAL: Only apply layout variations on ACTUAL layout changes, not widget switches
        if (!isInitialized) {
            console.log('🚫 HolisticTestimonial: Skipping layout variations - not initialized yet');
            return;
        }
        
        if (previousLayoutType === currentLayout) {
            console.log('🚫 HolisticTestimonial: Skipping layout variations - same layout type');
            return;
        }
        
        console.log(`🚀 HolisticTestimonial REAL layout change detected: ${previousLayoutType} → ${currentLayout}`);
        setPreviousLayoutType(currentLayout);
        
        // 🎯 LAYOUT-SPEZIFISCHE DEFAULTS
        const layoutVariations = {
            'classic-grid': {
                height: 'auto',
                heightMobile: 'auto',
                paddingTop: 60,
                paddingTopMobile: 40,
                paddingBottom: 60,
                paddingBottomMobile: 40,
                textAlignment: 'center',
                textAlignmentMobile: 'center',
                cardsPerRow: 2,
                cardSpacing: 20,
                cardPadding: 24,
                cardBorderRadius: 12,
                cardShadow: true,
                cardBorder: false,
                showTitleText: true,
                showStars: true,
                showAvatars: true
            },
            'linear-list': {
                height: 'auto',
                heightMobile: 'auto',
                paddingTop: 0,
                paddingTopMobile: 20,
                paddingBottom: 0,
                paddingBottomMobile: 20,
                textAlignment: 'left',
                textAlignmentMobile: 'left', // ✅ GEÄNDERT: Mobile auch left-aligned
                cardsPerRow: 1,
                cardSpacing: 0,
                cardPadding: 32,
                cardBorderRadius: 0,
                cardShadow: false,
                cardBorder: false,
                cardBackgroundColor: 'transparent',
                showTitleText: true,
                showStars: true,
                showAvatars: true
            },
            'elegant-cards': {
                height: 'auto',
                heightMobile: 'auto',
                paddingTop: 60,
                paddingTopMobile: 40,
                paddingBottom: 60,
                paddingBottomMobile: 40,
                textAlignment: 'center',
                textAlignmentMobile: 'center',
                cardsPerRow: 1,
                cardSpacing: 16,
                cardPadding: 40,
                cardBorderRadius: 16,
                cardShadow: true,
                cardBorder: false,
                cardBackgroundColor: '#ffffff',
                showTitleText: true,
                showStars: true,
                showAvatars: true
            }
        };
        
        const variations = layoutVariations[currentLayout];
        if (variations) {
            console.log(`🎯 Applying layout variations for ${currentLayout}:`, variations);
            
            // ✅ KRITISCH: BATCH UPDATE - Alle Werte auf einmal setzen
            const safePosition = widget.position !== undefined && widget.position !== null ? widget.position : 0;
            const updatedEditData = { ...editData, ...variations };
            
            // ✅ FIREBASE-SAFE: Position immer mitgesendet
            onUpdate({ ...updatedEditData, position: safePosition });
            
            console.log(`✅ HolisticTestimonial layout variations applied via batch update`);
        }
    }, [editData.layoutType, isInitialized, previousLayoutType, editData, onUpdate, widget.position]);
    
    // Testimonials Management
    const addTestimonial = () => {
        const currentTestimonials = editData.testimonials || [];
        const newTestimonial = {
            id: `testi${Date.now()}`,
            text: 'Amazing product! It really works as described.',
            author: 'John Doe',
            location: 'New York, USA',
            rating: 5,
            avatar: '/img/default-avatar.jpg'
        };
        const updatedTestimonials = [...currentTestimonials, newTestimonial];
        handleInputChange({ target: { name: 'testimonials', value: updatedTestimonials } });
    };

    const removeTestimonial = (index) => {
        const currentTestimonials = editData.testimonials || [];
        const updatedTestimonials = currentTestimonials.filter((_, i) => i !== index);
        handleInputChange({ target: { name: 'testimonials', value: updatedTestimonials } });
    };

    const updateTestimonial = (index, field, value) => {
        const currentTestimonials = editData.testimonials || [];
        const updatedTestimonials = [...currentTestimonials];
        updatedTestimonials[index] = { ...updatedTestimonials[index], [field]: value };
        handleInputChange({ target: { name: 'testimonials', value: updatedTestimonials } });
    };

    // Avatar Upload Handler
    const handleTestimonialAvatarUpload = async (index, file) => {
        if (!file) return;
        
        if (file.size > 2 * 1024 * 1024) {
            setTooBigFileInfo({ size: file.size, name: file.name });
            setShowImageTooBigPopup(true);
            return;
        }

        setUploadingState(index, true);
        
        try {
            const { getStorage, ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
            const { getAuth } = await import('firebase/auth');
            
            const storage = getStorage();
            const auth = getAuth();
            const userId = auth.currentUser.uid;
            const fieldName = `holistic_testimonial_${index}_avatar`;
            const imageRef = storageRef(storage, `pages/${userId}/${widget.id}/${fieldName}_${file.name}`);
            const uploadTask = uploadBytesResumable(imageRef, file);

            uploadTask.on('state_changed', 
                (snapshot) => {
                    // Upload progress
                }, 
                (error) => {
                    console.error('❌ Upload error:', error);
                    alert('Upload failed. Please try again.');
                    setUploadingState(index, false);
                }, 
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                        updateTestimonial(index, 'avatar', downloadURL);
                        setUploadingState(index, false);
                        console.log('✅ Holistic testimonial avatar uploaded:', downloadURL);
                    });
                }
            );
        } catch (error) {
            console.error('❌ Firebase import error:', error);
            alert('Upload failed. Please try again.');
            setUploadingState(index, false);
        }
    };


    // ✅ TEXT CONFIGURATION - NEW LAYOUT SYSTEM 2025
    const textConfig = [
        {
            fieldName: 'titleText',
            label: 'Testimonials Title',
            type: 'input',
            placeholder: 'What Our Customers Say...',
            fontField: 'titleFont',
            fontType: 'big',
            effectField: 'titleEffect'
        }
    ];

    // Color Configuration
    const colorConfig = [
        [
            { fieldName: 'titleColor', label: 'Title', defaultValue: '#2c3e50', icon: '/img/text-fat.svg' },
            { fieldName: 'testimonialTextColor', label: 'Testimonial Text', defaultValue: '#2c3e50', icon: '/img/text.svg' },
            { fieldName: 'authorNameColor', label: 'Author Name', defaultValue: '#2c3e50', icon: '/img/text.svg' }
        ],
        [
            { fieldName: 'authorLocationColor', label: 'Author Location', defaultValue: '#666666', icon: '/img/text.svg' },
            { fieldName: 'starColor', label: 'Stars', defaultValue: '#ffd700', icon: '/img/text.svg' },
            { fieldName: 'backgroundColor', label: 'Background', defaultValue: '#f8f9fa', icon: '/img/background.svg' }
        ],
        [
            { fieldName: 'cardBackgroundColor', label: 'Card Background', defaultValue: '#ffffff', icon: '/img/background.svg' },
            { fieldName: 'cardBorderColor', label: 'Card Border', defaultValue: '#f0f0f0', icon: '/img/background.svg' },
            { fieldName: 'avatarBorderColor', label: 'Avatar Border', defaultValue: '#e0e0e0', icon: '/img/background.svg' }
        ]
    ];

    // Font Configuration
    const fontConfig = [
        { label: 'Title', sizeField: 'titleSize', weightField: 'titleWeight', defaultSize: '32', defaultWeight: '600' },
        { label: 'Testimonial Text', sizeField: 'testimonialTextSize', weightField: 'testimonialTextWeight', defaultSize: '16', defaultWeight: '400' },
        { label: 'Author Name', sizeField: 'authorNameSize', weightField: 'authorNameWeight', defaultSize: '14', defaultWeight: '600' },
        { label: 'Author Location', sizeField: 'authorLocationSize', weightField: 'authorLocationWeight', defaultSize: '12', defaultWeight: '400' }
    ];

    // 🎯 CUSTOM LAYOUT PROPERTIES - Nur Avatar & Stars (responsive)
    const customLayoutProperties = [
        {
            section: 'Avatar & Stars',
            icon: 'fa-user-circle',
            properties: [
                {
                    id: 'avatarSize',
                    label: 'Avatar Size',
                    min: 24,
                    max: 80,
                    step: 4,
                    unit: 'px',
                    icon: '👤',
                    description: 'Size of testimonial avatars',
                    defaultValue: 48,
                    responsive: true // ✅ Mobile/Desktop Support
                },
                {
                    id: 'starSize',
                    label: 'Star Size',
                    min: 10,
                    max: 24,
                    step: 2,
                    unit: 'px',
                    icon: '⭐',
                    description: 'Size of rating stars',
                    defaultValue: 14,
                    responsive: true // ✅ Mobile/Desktop Support
                },
                {
                    id: 'starSpacing',
                    label: 'Star Spacing',
                    min: 1,
                    max: 8,
                    step: 1,
                    unit: 'px',
                    icon: '↔️',
                    description: 'Space between rating stars',
                    defaultValue: 2,
                    responsive: true // ✅ Mobile/Desktop Support
                }
            ]
        }
    ];

    // 🔧 ADVANCED SETTINGS - Nur funktionale Properties
    const advancedConfig = [
        // Text Alignment Controls - Responsive
        {
            fieldName: 'testimonialTextAlignment',
            label: 'Testimonial Text Alignment (Desktop)',
            type: 'select',
            defaultValue: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' }
            ],
            icon: '/img/text.svg',
            description: 'Text alignment for testimonial content on desktop'
        },
        {
            fieldName: 'testimonialTextAlignmentMobile',
            label: 'Testimonial Text Alignment (Mobile)',
            type: 'select',
            defaultValue: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' }
            ],
            icon: '/img/text.svg',
            description: 'Text alignment for testimonial content on mobile'
        },
        
        // Visibility Controls
        {
            fieldName: 'showTitleText',
            label: 'Show Title',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text-fat.svg'
        },
        {
            fieldName: 'showStars',
            label: 'Show Rating Stars',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text.svg'
        },
        {
            fieldName: 'showAvatars',
            label: 'Show Avatars',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        
        // Card Style Controls
        {
            fieldName: 'cardShadow',
            label: 'Card Shadow',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'cardBorder',
            label: 'Card Border',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'cardHoverShadow',
            label: 'Hover Effects',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        }
    ];

    return (
        <div>
            <div className="top-widget-edit-container">
                
                {/* ✅ LAYOUT SELECTOR 2025 */}
                <LayoutSelector2025
                    widgetType="holisticTestimonial"
                    selectedLayout={editData.layoutType || 'classic-grid'}
                    onLayoutChange={handleLayoutChange}
                />
                
                {/* ✅ TEXT SETTINGS */}
                <TextSettingsSection 
                    textConfig={textConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                    showButtonEffects={false}
                />

                {/* ✅ TESTIMONIALS MANAGEMENT */}
                <HolisticTestimonialsManagement 
                    testimonials={editData.testimonials || []}
                    addTestimonial={addTestimonial}
                    removeTestimonial={removeTestimonial}
                    updateTestimonial={updateTestimonial}
                    handleTestimonialAvatarUpload={handleTestimonialAvatarUpload}
                    uploadingStates={uploadingStates}
                    editData={editData}
                />

                {/* 🚀 REVOLUTIONARY LAYOUT SETTINGS - Standard Layout Properties */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={customLayoutProperties}
                />

                {/* 🔧 ADVANCED SETTINGS - Nur funktionale Properties */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                    defaultExpanded={false}
                />

                {/* ✅ MORE SETTINGS - Colors & Fonts only */}
                <MoreSettingsSection 
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={[]} // Empty - moved to Revolutionary Layout Settings
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 💾 SAVE AS TEMPLATE BUTTON */}
                <div className="save-template-section">
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                        title="Save this widget configuration as a reusable template"
                    >
                        <i className="fas fa-save"></i>
                        Save as Template
                    </button>
                </div>
                
            </div>

            {/* Image Too Big Popup */}
            <ImageTooBigPopup 
                isOpen={showImageTooBigPopup}
                onClose={() => setShowImageTooBigPopup(false)}
                fileSize={tooBigFileInfo.size}
                fileName={tooBigFileInfo.name}
                maxSize={2}
            />

            {/* 💾 SAVE TEMPLATE POPUP */}
            <SaveTemplatePopup
                isOpen={showSaveTemplatePopup}
                onClose={() => setShowSaveTemplatePopup(false)}
                widgetData={editData}
                originalWidgetType={widget.type}
            />
        </div>
    );
}

export default HolisticTestimonialEdit;